// packages/shared/src/types/user-roles.ts
/**
 * User roles and permissions for the MSB system
 * Centralized definition to avoid duplication across packages
 */

/**
 * User roles enum
 */
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  THERAPIST = 'therapist',
  PARENT = 'parent',
  CHILD = 'child',
  OBSERVER = 'observer'
}

/**
 * Permissions constants
 */
export const PERMISSIONS = {
  // User management
  USERS_CREATE: 'users.create',
  USERS_READ: 'users.read',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  
  // Client management
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_READ: 'clients.read',
  CLIENTS_UPDATE: 'clients.update',
  CLIENTS_DELETE: 'clients.delete',
  
  // Session management
  SESSIONS_CREATE: 'sessions.create',
  SESSIONS_READ: 'sessions.read',
  SESSIONS_UPDATE: 'sessions.update',
  SESSIONS_DELETE: 'sessions.delete',
  
  // Organization management
  ORG_MANAGE: 'organization.manage',
  ORG_INVITE: 'organization.invite',
  ORG_SETTINGS: 'organization.settings',
  
  // Reports and analytics
  REPORTS_VIEW: 'reports.view',
  ANALYTICS_VIEW: 'analytics.view',
  
  // System administration
  SYSTEM_ADMIN: 'system.admin',
} as const;

/**
 * Role-based permissions mapping
 */
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  [UserRole.SUPER_ADMIN]: Object.values(PERMISSIONS), // All permissions
  [UserRole.ADMIN]: [
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.CLIENTS_DELETE,
    PERMISSIONS.SESSIONS_CREATE,
    PERMISSIONS.SESSIONS_READ,
    PERMISSIONS.SESSIONS_UPDATE,
    PERMISSIONS.SESSIONS_DELETE,
    PERMISSIONS.ORG_MANAGE,
    PERMISSIONS.ORG_INVITE,
    PERMISSIONS.ORG_SETTINGS,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.ANALYTICS_VIEW,
  ],
  [UserRole.THERAPIST]: [
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.CLIENTS_READ,
    PERMISSIONS.CLIENTS_UPDATE,
    PERMISSIONS.CLIENTS_DELETE,
    PERMISSIONS.SESSIONS_CREATE,
    PERMISSIONS.SESSIONS_READ,
    PERMISSIONS.SESSIONS_UPDATE,
    PERMISSIONS.SESSIONS_DELETE,
    PERMISSIONS.REPORTS_VIEW,
  ],
  [UserRole.PARENT]: [
    PERMISSIONS.CLIENTS_READ, // Only their children
    PERMISSIONS.SESSIONS_READ, // Only their children's sessions
    PERMISSIONS.REPORTS_VIEW, // Only their children's reports
  ],
  [UserRole.CHILD]: [], // No direct system access
  [UserRole.OBSERVER]: [
    PERMISSIONS.SESSIONS_READ,
    PERMISSIONS.REPORTS_VIEW,
  ],
};

/**
 * Helper functions for role and permission management
 */
export function hasPermission(userPermissions: string[], requiredPermission: string): boolean {
  return userPermissions.includes(requiredPermission);
}

export function canAccessOrganization(userOrgId: string, targetOrgId: string, userRole: UserRole): boolean {
  // Super admins can access any organization
  if (userRole === UserRole.SUPER_ADMIN) return true;
  
  // Users can only access their own organization
  return userOrgId === targetOrgId;
}

export function canManageOrganization(isOrgOwner: boolean, userRole: UserRole): boolean {
  // Super admins can manage any organization
  if (userRole === UserRole.SUPER_ADMIN) return true;
  
  // Only organization owners can manage their organization
  return isOrgOwner;
}

export function getUserPermissions(role: UserRole, isOrgOwner: boolean): string[] {
  const basePermissions = ROLE_PERMISSIONS[role] || [];
  
  // Organization owners get additional permissions
  if (isOrgOwner) {
    return [
      ...basePermissions,
      PERMISSIONS.ORG_MANAGE,
      PERMISSIONS.ORG_INVITE,
      PERMISSIONS.ORG_SETTINGS,
    ];
  }
  
  return basePermissions;
}
